import { api, ApiError } from '@/api/baseApi'
import { getFirebaseAuth } from '@/lib/firebase'
import type {
  RiskPortalBankingResponse,
  RiskPortalBankSessionResponse,
  RiskPortalDocumentsResponse,
  RiskPortalExternalDocument,
  RiskPortalProfile,
} from '@/types/riskPortal.types'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api'

/**
 * Fetches the signed-in risk-assessment client's profile. Returns 401/403 (surfaced as an ApiError)
 * when the authenticated Firebase account is not an active risk-assessment user.
 */
export async function fetchMe(): Promise<RiskPortalProfile> {
  const response = await api.get<RiskPortalProfile>('/risk-assessment/portal/me')
  return response.data
}

/** Fetches the client's document checklist (requirement slots + their uploaded files + progress). */
export async function fetchDocuments(): Promise<RiskPortalDocumentsResponse> {
  const response = await api.get<RiskPortalDocumentsResponse>('/risk-assessment/portal/documents')
  return response.data
}

/** Removes one of the client's own uploaded documents. */
export async function deleteDocument(documentId: string): Promise<void> {
  await api.delete(`/risk-assessment/portal/documents/${documentId}`)
}

/** Fetches the client's bank-connection state (availability, status, linked accounts). */
export async function fetchBanking(): Promise<RiskPortalBankingResponse> {
  const response = await api.get<RiskPortalBankingResponse>('/risk-assessment/portal/banking')
  return response.data
}

/** Creates a fresh single-use Stripe Financial Connections session for the client's group. */
export async function createBankSession(): Promise<RiskPortalBankSessionResponse> {
  const response = await api.post<RiskPortalBankSessionResponse>(
    '/risk-assessment/portal/banking/sessions',
  )
  return response.data
}

/**
 * Finalizes a Stripe Financial Connections session after the client linked accounts in the Stripe
 * modal: the backend pulls the linked accounts + transactions and returns the updated banking state.
 */
export async function completeBankSession(sessionId: string): Promise<RiskPortalBankingResponse> {
  const response = await api.post<RiskPortalBankingResponse>(
    `/risk-assessment/portal/banking/sessions/${sessionId}/complete`,
  )
  return response.data
}

/**
 * Sets (or clears, when passed an empty string) the optional venue name on one of the client's linked bank
 * accounts — naming the venue it funds so the reviewer can map it to a location. Returns the updated banking
 * state. Disconnecting an account is intentionally not available to clients; only Mezza can remove a connection.
 */
export async function setBankAccountVenueName(
  accountId: string,
  venueName: string,
): Promise<RiskPortalBankingResponse> {
  const response = await api.put<RiskPortalBankingResponse>(
    `/risk-assessment/portal/banking/accounts/${encodeURIComponent(accountId)}/venue-name`,
    { venueName },
  )
  return response.data
}

async function getAuthToken(forceRefresh: boolean): Promise<string | null> {
  const currentUser = getFirebaseAuth().currentUser
  if (!currentUser) return null
  return currentUser.getIdToken(forceRefresh)
}

/** Thrown when an upload is aborted via its `AbortSignal` (e.g. the user hit cancel). */
export class UploadCancelledError extends Error {
  constructor() {
    super('Upload cancelled')
    this.name = 'UploadCancelledError'
  }
}

/**
 * Uploads a single file against a requirement slot. Uses XMLHttpRequest (not fetch) so we get real
 * per-file byte progress for the upload UI. Retries once with a force-refreshed token on a 401 so an
 * expired id token doesn't surface as a failure mid-session. Pass a `signal` to let the caller abort
 * the in-flight upload — the returned promise then rejects with an {@link UploadCancelledError}.
 */
export function uploadDocument(
  requirementKey: string,
  file: File,
  onProgress?: (percent: number) => void,
  signal?: AbortSignal,
): Promise<RiskPortalExternalDocument> {
  return sendUpload(requirementKey, file, onProgress, false, signal)
}

async function sendUpload(
  requirementKey: string,
  file: File,
  onProgress: ((percent: number) => void) | undefined,
  isRetry: boolean,
  signal?: AbortSignal,
): Promise<RiskPortalExternalDocument> {
  if (signal?.aborted) {
    throw new UploadCancelledError()
  }

  const token = await getAuthToken(isRetry)

  return new Promise<RiskPortalExternalDocument>((resolve, reject) => {
    const formData = new FormData()
    formData.append('requirementKey', requirementKey)
    formData.append('file', file)

    const xhr = new XMLHttpRequest()
    xhr.open('POST', `${API_BASE_URL}/risk-assessment/portal/documents`)
    if (token) {
      xhr.setRequestHeader('Authorization', `Bearer ${token}`)
    }

    // Wire the abort signal to the in-flight request so a cancel actually stops the upload.
    const onAbort = () => xhr.abort()
    signal?.addEventListener('abort', onAbort)
    const cleanup = () => signal?.removeEventListener('abort', onAbort)

    if (onProgress) {
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          onProgress(Math.round((event.loaded / event.total) * 100))
        }
      }
    }

    xhr.onload = () => {
      cleanup()
      if (xhr.status === 401 && !isRetry) {
        // Token likely expired — retry once with a forced refresh.
        sendUpload(requirementKey, file, onProgress, true, signal).then(resolve).catch(reject)
        return
      }

      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(JSON.parse(xhr.responseText) as RiskPortalExternalDocument)
        } catch {
          reject(new ApiError(xhr.status, 'Invalid response', xhr.responseText))
        }
        return
      }

      const data = safeParse(xhr.responseText)
      reject(new ApiError(xhr.status, xhr.statusText || 'Upload failed', data))
    }

    xhr.onerror = () => {
      cleanup()
      reject(new ApiError(0, 'Network error'))
    }
    xhr.onabort = () => {
      cleanup()
      reject(new UploadCancelledError())
    }
    xhr.send(formData)
  })
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text)
  } catch {
    return null
  }
}
